import React, { useMemo, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";

import { DISMISSED } from "../EvidenceStatus";

import { amber, green } from "@mui/material/colors";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { IconButton, Popover, Grid, Icon, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { mapToDisplay } from "../CameraNames";

import IncidentEvidenceItemVideo from "./IncidentEvidenceItemVideo";

const useStyles = makeStyles((theme) => ({
  retrievalStatus: {
    display: "flex",
  },
  media: {
    position: "relative",
    paddingBottom: "56.25%",
    overflow: "hidden",
  },
  mediaImage: {
    cursor: "pointer",
    marginBottom: theme.spacing(1),
  },
  videoDownload: {
    textAlign: "center",
    paddingBottom: 0,
  },
  mediaVideo: {
    padding: 0,
  },
  html5Video: {
    maxWidth: "100%",
  },
  pendingReviewChip: {
    backgroundColor: amber[500],
  },
  acceptedChip: {
    backgroundColor: green[500],
  },
  leftIcon: {
    marginRight: theme.spacing(2),
  },
  videoCam: {
    fontSize: 60,
    color: theme.palette.divider,
  },
}));

const InfoPopover = ({ request }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  return (
    <>
      <IconButton
        color="info"
        aria-label="Show request info"
        onClick={handleClick}
        sx={{ padding: 0 }}
      >
        <InfoIcon fontSize="small" />
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Typography sx={{ p: 2 }}>
          Requested by {request.createdByUser?.name ?? "Driver"} on{" "}
          {moment(request.createdAt).format("lll")}
          <Box
            sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}
            typography={"body2"}
          >
            Cameras:{" "}
            {request?.cameras
              ?.map((channel) => mapToDisplay(channel))
              .join(", ")}
          </Box>
          {request.status !== "COMPLETE" &&
            Array.isArray(request.completedCameras) &&
            (request.status === "PARTIAL" ||
              request.completedCameras.length > 0) && (
              <Box typography={"body2"} sx={{ marginTop: "8px" }}>
                <div>
                  <Box component="span" sx={{ color: "success.main" }}>
                    Completed:
                  </Box>{" "}
                  {request.completedCameras.length
                    ? request.completedCameras
                        .map((channel) => mapToDisplay(channel))
                        .join(", ")
                    : "none"}
                </div>
                {(request.cameras ?? []).some(
                  (c) => !request.completedCameras.includes(c),
                ) && (
                  <div>
                    {request.status === "PROGRESSING" ? (
                      <Box component="span" sx={{ color: "text.secondary" }}>
                        Pending:
                      </Box>
                    ) : (
                      <Box component="span" sx={{ color: "error.main" }}>
                        {request.status === "CANCELED"
                          ? "Cancelled:"
                          : "Failed:"}
                      </Box>
                    )}{" "}
                    {(request.cameras ?? [])
                      .filter((c) => !request.completedCameras.includes(c))
                      .map((channel) => mapToDisplay(channel))
                      .join(", ")}
                  </div>
                )}
              </Box>
            )}
        </Typography>
      </Popover>
    </>
  );
};

const IncidentEvidence = ({
  attachedEvidence,
  updateEvidence,
  footageRequests,
  showDismissed,
  editable,
  syncPlayFootage,
}) => {
  const classes = useStyles();
  const [onScreenAttachedEvidence, setOnScreenAttachedEvidence] = useState([]);
  const [syncMasterIndex, setSyncMasterIndex] = useState(-1);

  useEffect(() => {
    const toDisplay = attachedEvidence.filter(
      (attachment) => showDismissed || attachment.status !== DISMISSED,
    );

    setOnScreenAttachedEvidence(toDisplay);
  }, [attachedEvidence, showDismissed]);

  const sortedAttachedEvidence = useMemo(() => {
    return onScreenAttachedEvidence
      .sort((a, b) => new Date(a.startAt) - new Date(b.startAt))
      .map((item, index) => ({ ...item, index: index + 1 }));
  }, [onScreenAttachedEvidence]);

  const elementsRef = useRef(new Map());

  const handleVideoUserInitiatedPlay = (masterIndex) => {
    if (!syncPlayFootage) {
      return;
    }

    // elementsRef.current.forEach((video) => video.pause());

    setSyncMasterIndex(masterIndex);

    // syncVideosWith(masterIndex);
    const baseTimestamp = getBaseTimestamp(masterIndex);

    elementsRef.current.forEach((video, index) => {
      if (masterIndex === index) return;

      video.mute();

      const currentTime = tryGetCurrentTimeDelta(baseTimestamp, index);
      if (!currentTime) {
        // console.log("pausing video", { index });
        video.pause();
        return;
      }

      video.play();
      video.currentTime(currentTime);
    });
  };

  const getBaseTimestamp = (masterIndex) => {
    const evidence = sortedAttachedEvidence[masterIndex];

    const baseCurrentTime = elementsRef.current.get(masterIndex).currentTime();
    const baseTimestamp = moment(evidence.startAt).add(
      baseCurrentTime,
      "seconds",
    );

    return baseTimestamp;
  };

  const tryGetCurrentTimeDelta = (baseTimestamp, index) => {
    const videoAttachment = sortedAttachedEvidence[index];

    if (!videoAttachment?.startAt || !videoAttachment?.endAt) return;

    const startTime = moment(videoAttachment.startAt);
    const endTime = moment(videoAttachment.endAt);

    const isBetween = baseTimestamp.isBetween(startTime, endTime, null, "[]");

    if (!isBetween) return;

    const currentTime = baseTimestamp.unix() - startTime.unix();

    return currentTime;
  };

  const handleMasterTimeUpdate = (masterIndex, time) => {
    if (!syncPlayFootage) return;

    const baseTimestamp = getBaseTimestamp(masterIndex);

    elementsRef.current.forEach((video, index) => {
      if (masterIndex === index) return;

      const currentTime = tryGetCurrentTimeDelta(baseTimestamp, index);
      if (!currentTime) return;
      video.currentTime(currentTime);
    });
  };

  const handleVideoUserInitiatedPause = (idx) => {
    setSyncMasterIndex(-1);
    if (!syncPlayFootage) return;

    // console.log("user initiated pausing video", { idx });
    elementsRef.current.forEach((video) => video.pause());
  };

  return (
    <div>
      {Boolean(footageRequests.length) && (
        <div>
          <Box component="ul" sx={{ padding: 0 }}>
            {footageRequests.map((request, idx) => (
              <Box
                key={idx}
                component="li"
                sx={{ listStyleType: "none", marginBottom: "8px" }}
              >
                <Box sx={{ display: "flex" }}>
                  <Icon
                    className={classes.leftIcon}
                    title={request.status}
                    color={request.status === "PARTIAL" ? "warning" : undefined}
                  >
                    {request.status === "PROGRESSING"
                      ? "cloud"
                      : request.status === "COMPLETE" ||
                          request.status === "PARTIAL"
                        ? "cloud_done"
                        : request.status === "CANCELED" ||
                            request.status === "ERROR"
                          ? "error"
                          : "cloud_queue"}
                  </Icon>
                  <div>
                    {moment(request.footageStartAt).format("lll")} -{" "}
                    {moment(request.footageEndAt).format("LT")}
                    <Box
                      sx={{ display: "flex", gap: "4px" }}
                      typography={"body2"}
                    >
                      Cameras:{" "}
                      {request?.cameras
                        ?.map((channel) => mapToDisplay(channel))
                        .join(", ")}
                    </Box>
                  </div>
                  <Box sx={{ marginLeft: "16px" }}>
                    <InfoPopover request={request} />
                  </Box>
                  {request.status === "PARTIAL" && (
                    <Box sx={{ marginLeft: "16px" }} typography={"body2"}>
                      <b>
                        {Math.round(
                          ((request.jobPercentComplete ?? 0) *
                            (request.cameras?.length ?? 0)) /
                            100,
                        )}
                        /{request.cameras?.length ?? 0} cameras
                      </b>{" "}
                      - <em>Partially completed</em>
                    </Box>
                  )}
                  {request.status === "PROGRESSING" && (
                    <Box
                      sx={{
                        marginLeft: "16px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "8px",
                      }}
                    >
                      <div>
                        <CircularProgress size={14} />
                      </div>
                      <div>
                        <b>{request.jobPercentComplete}%</b> -{" "}
                        <em>
                          {request.phase === "QUERYING" && "Querying device"}
                          {request.phase === "DOWNLOADING" && "Downloading"}
                          {request.phase === "PROCESSING" && "Processing"}
                          {request.phase === "UPLOADING" && "Uploading"}
                        </em>
                      </div>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </div>
      )}
      {!Boolean(attachedEvidence.length) && <p>No evidence attached</p>}
      <Grid spacing={2} container>
        {sortedAttachedEvidence.map((attachment, idx) => (
          <Grid
            key={`file-${attachment.source.src}-${idx}`}
            item
            xs={12}
            sm={6}
            md={4}
            xl={3}
          >
            <IncidentEvidenceItemVideo
              attachment={attachment}
              updateEvidence={updateEvidence}
              editable={editable}
              classes={classes}
              ref={(el) => {
                el
                  ? elementsRef.current && elementsRef.current.set(idx, el)
                  : elementsRef.current && elementsRef.current.delete(idx);
              }}
              isSyncMaster={syncPlayFootage && syncMasterIndex === idx}
              synchronisedPlayback={syncPlayFootage}
              onUserInitiatedPlay={() => handleVideoUserInitiatedPlay(idx)}
              onUserInitiatedPause={() => handleVideoUserInitiatedPause(idx)}
              onMasterTimeUpdate={(time) => handleMasterTimeUpdate(idx, time)}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

IncidentEvidence.propTypes = {
  attachedEvidence: PropTypes.array.isRequired,
  showDismissed: PropTypes.bool,
  updateEvidence: PropTypes.func.isRequired,
};

export default IncidentEvidence;
